import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode
} from 'react'
import { getRedirectResult, onAuthStateChanged, type User } from 'firebase/auth'
import { useSyncUser } from '@batical/reducer/src'
import { auth } from 'src/shims/firebase'
import { FIREBASE_REDIRECT_ERROR_KEY } from 'src/utils/authBrowser'
import { authWebLog, reportWebNonFatalError } from 'src/utils/firebaseWebErrorReport'

type AuthState = { user: User | null; loading: boolean }
const AuthContext = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const { sync } = useSyncUser()
  const syncRef = useRef(sync)
  syncRef.current = sync

  /** Ne pas attendre `sync` ici : il est lent (RTDB / Firestore / Stripe) et bloquait `setUser`,
   * donc après `navigate('/dashboard')` RequireAuth voyait encore `user === null` et renvoyait au login.
   * `loading` reste true jusqu’à fin de `getRedirectResult` : sinon onAuthStateChanged peut livrer null
   * avant la fin du redirect OAuth (Safari / iOS), et RequireAuth renvoie au login en boucle. */
  const handleAuthChange = useCallback((u: User | null) => {
    setUser(u)
    authWebLog('auth_state_changed', {
      hasUser: !!u,
      uid: u?.uid ?? null,
      isAnonymous: u?.isAnonymous ?? null,
      providerIds: u?.providerData?.map((p) => p.providerId).join(',') ?? '',
      emailPresent: !!u?.email,
    })
    if (u) {
      void syncRef.current(u as any).catch((err: unknown) => {
        authWebLog('sync_user_failed', {
          uid: u.uid,
          message: String((err as Error)?.message ?? err).slice(0, 240),
        })
        reportWebNonFatalError('auth_sync_user', err)
      })
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const endRedirectPhase = () => {
      if (!cancelled) setLoading(false)
    }

    // Abonnement immédiat (ne pas sérialiser derrière getRedirectResult : iOS peut ralentir la promesse).
    const unsubscribe = onAuthStateChanged(auth, handleAuthChange)

    const redirectWaitMs = 12000
    const safetyTimer = window.setTimeout(endRedirectPhase, redirectWaitMs)

    void getRedirectResult(auth)
      .then((result) => {
        if (cancelled) return
        if (result?.user) {
          authWebLog('redirect_result_ok', {
            uid: result.user.uid,
            providerIds: result.user.providerData.map((p) => p.providerId).join(','),
          })
        } else if (import.meta.env.DEV) {
          authWebLog('redirect_result_null', {
            hint: 'Pas de jeton redirect (chargement normal ou déjà consommé).',
          })
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const e = err as { code?: string; message?: string }
        authWebLog('redirect_result_error', {
          code: e?.code ?? 'unknown',
          message: (e?.message ?? String(err)).slice(0, 280),
        })
        reportWebNonFatalError('auth_getRedirectResult', err)
        try {
          sessionStorage.setItem(
            FIREBASE_REDIRECT_ERROR_KEY,
            JSON.stringify({
              code: e?.code ?? 'unknown',
              message: (e?.message ?? String(err)).slice(0, 600),
              t: Date.now()
            })
          )
        } catch {
          /* sessionStorage indisponible (mode privé) */
        }
      })
      .finally(() => {
        window.clearTimeout(safetyTimer)
        endRedirectPhase()
      })

    return () => {
      cancelled = true
      window.clearTimeout(safetyTimer)
      unsubscribe()
    }
  }, [handleAuthChange])

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (ctx === undefined) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
